import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, INVALID_ID } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { SurveyQuestionAnswerInfo } from "./surveyQuestionAnswerListSlice";

export interface SurveyQuestionAnswerInsUptState {
    state: LoadStateInfo;
    surveyQuestionAnswer: SurveyQuestionAnswerInfo;
}

const initialState: SurveyQuestionAnswerInsUptState = {
    state: notStarted(),
    surveyQuestionAnswer: emptySurveyQuestionAnswer(),
};

function emptySurveyQuestionAnswer(): SurveyQuestionAnswerInfo {
    return {
        id: INVALID_ID,
        surveyId: "",
        surveyQuestionId: "",
        surveyQuestionOptionId: undefined,
        studentId: "",
        userId: "",
        answeredAt: new Date().toISOString(),
    };
}

export interface SurveyQuestionAnswerInsertRequest {
    surveyQuestionAnswer: SurveyQuestionAnswerInfo;
}

export const surveyQuestionAnswerInsert = createAsyncThunk(
    "surveyQuestionAnswerInsert",
    async (param: SurveyQuestionAnswerInsertRequest, thunkAPI) => {
        console.debug("insert survey question answer");
        const response = await fetch(`${getBackendUrl()}/api/survey/question/answer`, {
            method: "POST",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(param.surveyQuestionAnswer)
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export interface SurveyQuestionAnswerUpdateRequest {
    answerId: string;
    surveyQuestionAnswer: SurveyQuestionAnswerInfo;
}

export const surveyQuestionAnswerUpdate = createAsyncThunk(
    "surveyQuestionAnswerUpdate",
    async (param: SurveyQuestionAnswerUpdateRequest, thunkAPI) => {
        console.debug("update survey question answer");
        const response = await fetch(`${getBackendUrl()}/api/survey/question/answer/${param.answerId}`, {
            method: "PUT",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(param.surveyQuestionAnswer)
        });
        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export interface SurveyQuestionAnswerDeleteRequest {
    answerId: string;
}

export const surveyQuestionAnswerDelete = createAsyncThunk(
    "surveyQuestionAnswerDelete",
    async (param: SurveyQuestionAnswerDeleteRequest, thunkAPI) => {
        console.debug("delete survey question answer");
        const response = await fetch(`${getBackendUrl()}/api/survey/question/answer/${param.answerId}`, {
            method: "DELETE",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });
        if (response.ok) {
            return param.answerId;
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export const surveyQuestionAnswerInsUpdSlice = createSlice({
    name: "surveyQuestionAnswerInsUpd",
    initialState,
    reducers: {
        reset: (state) => {
            state.state = notStarted();
            state.surveyQuestionAnswer = emptySurveyQuestionAnswer();
        },
        setSurveyQuestionAnswer: (state, action: PayloadAction<SurveyQuestionAnswerInfo>) => {
            state.surveyQuestionAnswer = action.payload;
        },
        updateField: (state, action: PayloadAction<{ field: keyof SurveyQuestionAnswerInfo; value: any }>) => {
            (state.surveyQuestionAnswer as any)[action.payload.field] = action.payload.value;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(surveyQuestionAnswerInsert.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyQuestionAnswerInsert.fulfilled, (state, action) => {
                state.state = loaded();
                state.surveyQuestionAnswer = action.payload;
            })
            .addCase(surveyQuestionAnswerInsert.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(surveyQuestionAnswerUpdate.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyQuestionAnswerUpdate.fulfilled, (state, action) => {
                state.state = loaded();
                state.surveyQuestionAnswer = action.payload;
            })
            .addCase(surveyQuestionAnswerUpdate.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(surveyQuestionAnswerDelete.pending, (state) => {
                state.state = loading();
            })
            .addCase(surveyQuestionAnswerDelete.fulfilled, (state) => {
                state.state = loaded();
                state.surveyQuestionAnswer = emptySurveyQuestionAnswer();
            })
            .addCase(surveyQuestionAnswerDelete.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const { reset, setSurveyQuestionAnswer, updateField } = surveyQuestionAnswerInsUpdSlice.actions;

export const selectSurveyQuestionAnswerInsUpd = (state: RootState) => state.surveyQuestionAnswerInsUpd;

export default surveyQuestionAnswerInsUpdSlice.reducer;
